import type React from "react"

import { getCitationFilePath } from "@/api/api"
import { isPersonalLibraryCitation } from "@/features/ai-explorer/utils/personal-search-utils"

import { CitationIcon } from "./citation-icon"
import { getCitationType } from "./citation.utils"

/**
 * Creates a custom `a` component for ReactMarkdown that renders
 * `[N](cite:INDEX)` links as citation buttons. This replaces the old
 * approach of injecting raw `<button>` HTML, which broke inside GFM table cells.
 */
export function createCitationLinkComponent(
  getCitations: () => string[],
  getCitationSection: () => string,
  messageIndex?: number
) {
  return function CitationLink({
    href,
    children,
    node: _node,
    ...props
  }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) {
    if (href?.startsWith("cite:")) {
      const citationIdx = parseInt(href.slice(5), 10)
      const citation = getCitations()[citationIdx] as string | undefined
      if (citation) {
        const personalLibrary = isPersonalLibraryCitation(citation)
        const path = personalLibrary
          ? undefined
          : getCitationFilePath(citation, getCitationSection())
        const citationType = getCitationType(citation)
        const dispatchHover = (value: string | null) => {
          if (typeof window === "undefined") return
          window.dispatchEvent(
            new CustomEvent("deep:citation-hover", {
              detail: { citation: value },
            })
          )
        }
        return (
          <span
            className="supContainer !mt-1"
            data-citation-index={citationIdx}
            data-citation-path={path}
            data-citation-raw={citation}
            data-message-index={messageIndex}
            role="button"
            tabIndex={0}
            onBlur={() => dispatchHover(null)}
            onFocus={() => dispatchHover(citation)}
            onMouseEnter={() => dispatchHover(citation)}
            onMouseLeave={() => dispatchHover(null)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                e.currentTarget.click()
              }
            }}
          >
            <sup className="inline-flex items-center justify-center gap-1 px-[0.3125rem] py-0.5">
              <span className="mt-px inline-block leading-none">{children}</span>
              <span className="inline-flex items-center">
                <CitationIcon citation={citation} className="size-3" type={citationType} />
              </span>
            </sup>
          </span>
        )
      }
    }
    return (
      <a href={href} {...props}>
        {children}
      </a>
    )
  }
}
